// frontend/src/pages/AdminPage.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminOracleState3D from "../components/AdminOracleState3D";
import { secureFetch, getToken } from "../utils/auth";
import { lireJson } from "../utils/reponseHttp";
import { freshness } from "../utils/freshness";
import "../styles/admin-oracle.css";
import { getBackendUrl } from "../config";
import { useAuth } from "../context/AuthContext";

const backendUrl = getBackendUrl();
const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
const host = window.location.host;
const baseWs = `${protocol}//${host}`;

function hexToRgb(hex) {
  const bigint = parseInt(hex.slice(1), 16);
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
}

// ────────────────────────────────────────────────────────────
// Banc comparatif — second niveau de lecture de « Test Puissance ».
// Le rendu est générique : la source de vérité des axes et des mesures reste
// backend/tools/banc_comparatif.py. On affiche ce qu'il mesure, on ne le
// redéfinit pas ici — un axe ajouté là-bas apparaît ici sans modification.
// ────────────────────────────────────────────────────────────
function formaterValeur(v) {
  if (v === null || v === undefined) return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "aucun";
  if (typeof v === "boolean") return v ? "oui" : "non";
  return String(v);
}

// Seule liberté prise ici : la couleur. Les notes elles-mêmes viennent du banc.
function couleurNote(note) {
  if (note === null || note === undefined) return "#777";
  if (note >= 90) return "#0f9";
  if (note >= 70) return "#fa0";
  return "#f55";
}

function BancComparatif({ etat }) {
  if (!etat) return null;

  const cadre = { marginTop: "10px", paddingTop: "8px", borderTop: "1px dashed #555" };
  const entete = (
    <div style={{ color: "#ff0055", fontSize: "0.7rem", letterSpacing: "0.08em", marginBottom: "6px" }}>
      AUDIT COMPARATIF
    </div>
  );

  if (etat.statut === "en_cours")
    return <div style={cadre}>{entete}<div style={{ color: "#888", fontSize: "0.8rem" }}>⏳ Mesure en cours (~30 s)…</div></div>;

  if (etat.statut === "echec")
    return <div style={cadre}>{entete}<div style={{ color: "#f55", fontSize: "0.8rem" }}>❌ {etat.erreur}</div></div>;

  if (etat.statut !== "termine" || !etat.resultat) return null;

  const { protocole = {}, synthese_oracle: synthese = {}, verdicts = {}, oracle = {},
          provenance = {}, empreinte_sha256: empreinte } = etat.resultat;
  const axes = protocole.axes || [];
  const scores = synthese.scores || {};
  const criteres = synthese.criteres || {};
  const notation = protocole.notation || {};

  return (
    <div style={cadre}>
      {entete}

      {/* Score global — calculé par le banc, jamais ici. */}
      <div style={{ display: "flex", alignItems: "baseline", gap: "8px", marginBottom: "2px" }}>
        <span style={{ color: "#888", fontSize: "0.7rem" }}>SCORE GLOBAL</span>
        <span style={{ color: couleurNote(scores.global), fontSize: "1.3rem", fontWeight: "bold" }}>
          {scores.global === null || scores.global === undefined ? "—" : scores.global}
        </span>
        <span style={{ color: "#666", fontSize: "0.65rem" }}>/100 · {notation.global?.regle}</span>
      </div>
      <div style={{ color: "#888", fontSize: "0.7rem", marginBottom: "8px" }}>
        {synthese.modules_evalues} modules · fenêtre {protocole.fenetre} · {protocole.repetitions} répétitions · {etat.duree}s
      </div>

      {axes.map((axe) => {
        const mesures = synthese[axe];
        if (!mesures || typeof mesures !== "object") return null;
        const verdict = verdicts[axe] || {};
        const manques = Object.entries(criteres[axe] || {}).filter(([, ok]) => !ok);
        return (
          <div key={axe} style={{ marginBottom: "8px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "8px" }}>
              <span style={{ color: "#0ff", fontSize: "0.72rem", textTransform: "uppercase" }}>
                {axe} <span style={{ color: "#555", fontSize: "0.6rem" }}>{notation[axe]?.famille}</span>
              </span>
              <span style={{ color: couleurNote(scores[axe]), fontSize: "0.8rem", fontWeight: "bold" }}>
                {scores[axe] === null || scores[axe] === undefined ? "—" : scores[axe]}
              </span>
            </div>
            {Object.entries(mesures).map(([cle, valeur]) => (
              <div key={cle} style={{ display: "flex", justifyContent: "space-between", fontSize: "0.7rem", gap: "8px" }}>
                <span style={{ color: "#777" }}>{cle}</span>
                <span style={{ color: "#0f9", textAlign: "right", wordBreak: "break-word" }}>{formaterValeur(valeur)}</span>
              </div>
            ))}
            {/* Un critère non satisfait explique la note : on le nomme. */}
            {manques.map(([critere]) => (
              <div key={critere} style={{ color: "#fa0", fontSize: "0.65rem" }}>✗ {critere}</div>
            ))}
            {verdict.oracle_superieur === null && (
              <div style={{ color: "#666", fontSize: "0.65rem", fontStyle: "italic" }}>verdict : {verdict.motif}</div>
            )}
          </div>
        );
      })}

      <div style={{ fontSize: "0.7rem", color: (oracle.incidents || []).length ? "#fa0" : "#777" }}>
        incidents relevés : {(oracle.incidents || []).length}
      </div>

      {/* Provenance : de quoi rattacher ces notes à un état exact du système.
          L'empreinte détecte une altération de l'artefact ; elle ne signe rien. */}
      <div style={{ marginTop: "6px", paddingTop: "4px", borderTop: "1px solid #333", color: "#666", fontSize: "0.62rem" }}>
        <div>passage {provenance.horodatage_utc} · {provenance.hote}</div>
        <div>protocole {String(provenance.empreinte_protocole_sha256 || "").slice(0, 12)}… · résultat {String(empreinte || "").slice(0, 12)}…</div>
        <div>révision {provenance.revision_git || "non fournie"} · python {provenance.python} · numpy {provenance.numpy}</div>
      </div>
    </div>
  );
}

function useHighlightOnChange(value, duration = 600) {
  const [highlight, setHighlight] = useState(false);
  const prev = useRef(value);
  useEffect(() => {
    if (value !== prev.current) {
      setHighlight(true);
      prev.current = value;
      const timer = setTimeout(() => setHighlight(false), duration);
      return () => clearTimeout(timer);
    }
  }, [value, duration]);
  return highlight;
}

// Rendu du panneau "Test Puissance" enrichi. Ne calcule rien : chaque nombre
// affiché vient tel quel de la réponse de `/api/system/stress-test`, qui
// transmet elle-même la sortie de `tools/banc_comparatif.rapport()` et
// `synthese_api()` sans les retoucher. Les scores (global_score, quality,
// robustness, auditability, reproducibility, performance) sont déjà calculés
// côté banc — ce composant se contente de les afficher, avec une couleur
// selon leur valeur (pur affichage, ça ne change aucun nombre).
function scoreColor(v) {
  if (v == null) return "#666";
  if (v >= 80) return "#0f9";
  if (v >= 50) return "#fc5";
  return "#f50";
}

function BancPanel({ result }) {
  if (!result) return null;
  const banc = result.banc;
  const erreur = result.banc_erreur;
  if (!banc && !erreur) return null;

  if (erreur) {
    return (
      <div style={{ marginTop: "8px", fontSize: "0.75rem", color: "#f50" }}>
        ⚠️ Banc indisponible : {erreur}
      </div>
    );
  }

  const q = banc.qualite, r = banc.robustesse, a = banc.auditabilite, rep = banc.reproductibilite;
  const robusteOk = r.incidents === 0 && r.valeurs_non_finies === 0
    && r.valeurs_hors_borne === 0 && r.erreurs === 0 && r.stress_non_finis === 0;
  const auditOk = a.pseudo_mesures_detectees === 0
    && a.chemin_scene_mesure_api_complet === a.modules_geometriques;

  const sousScores = [
    ["Perf.", result.performance], ["Qualité", result.quality],
    ["Robust.", result.robustness], ["Audit.", result.auditability],
    ["Reprod.", result.reproducibility],
  ];

  return (
    <div style={{
      marginTop: "8px", padding: "8px", background: "#111", border: "1px solid #333",
      borderRadius: "4px", maxHeight: "260px", overflowY: "auto",
      fontSize: "0.75rem", lineHeight: 1.5,
    }}>
      {/* SYNTHÈSE — score global + sous-scores, champs plats de la réponse */}
      {result.global_score != null && (
        <>
          <div style={{ display: "flex", alignItems: "baseline", gap: "8px", marginBottom: "4px" }}>
            <span style={{ color: "#888" }}>Score global</span>
            <b style={{ color: scoreColor(result.global_score), fontSize: "1.1rem" }}>
              {result.global_score}/100
            </b>
          </div>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "6px" }}>
            {sousScores.map(([label, v]) => (
              <span key={label} style={{ color: scoreColor(v) }}>
                {label} {v == null ? "—" : `${v}`}
              </span>
            ))}
          </div>
        </>
      )}

      {/* DÉTAIL — sortie brute du banc, pour audit */}
      <div style={{ color: "#888", marginBottom: "4px", borderTop: "1px solid #333", paddingTop: "4px" }}>
        Banc comparatif — {banc.modules_evalues} modules
        {result.benchmark_duration_s != null && ` · ${result.benchmark_duration_s}s total`}
        {result.banc_duree_s != null && ` (banc ${result.banc_duree_s}s)`}
      </div>
      <div>
        <b style={{ color: "#0af" }}>Qualité</b> — variance rigides max {q.scenes_rigides_variance_max ?? "—"},
        amplitude mobiles min {q.scenes_mobiles_amplitude_min ?? "—"}, écart cadence max {q.ecart_relatif_cadence_max ?? "—"}
      </div>
      <div>
        <b style={{ color: robusteOk ? "#0f9" : "#f50" }}>Robustesse</b> — {robusteOk ? "OK" : "voir détail"} (
        {r.incidents} incident(s), {r.valeurs_non_finies} NaN/inf, {r.valeurs_hors_borne} hors borne,
        {" "}{r.stress_sains}/{r.stress_modules_testes} sains sous stress)
      </div>
      <div>
        <b style={{ color: auditOk ? "#0f9" : "#f50" }}>Auditabilité</b> — {auditOk ? "OK" : "voir détail"} (
        {a.chemin_scene_mesure_api_complet}/{a.modules_geometriques} chemins raccordés,
        {" "}{a.pseudo_mesures_detectees} pseudo-mesure(s))
      </div>
      <div>
        <b style={{ color: "#0af" }}>Reproductibilité</b> — {rep.modules_conformes ?? "—"}/{rep.modules_notes ?? "—"} modules conformes,
        {" "}dispersion max {rep.dispersion_max ?? "—"}
        {" "}(hors graine {rep.dispersion_max_hors_graine ?? "—"})
        {rep.semence_fixe == null && " — scènes tirées à la création : stables en exécution, variables au redémarrage"}
      </div>
      {result.banc_verdicts && (
        <div style={{ color: "#666", marginTop: "4px" }}>
          Concurrent : {result.banc_verdicts.qualite?.motif || "non mesuré"}
        </div>
      )}
      {result.referentiel_externe && (
        <div style={{ color: "#666", marginTop: "4px" }}>
          Référentiel externe : {result.referentiel_externe.source} ({result.referentiel_externe.nature})
        </div>
      )}
    </div>
  );
}

function getModuleState(value, prevValue) {
  if (value === undefined || value === null) return "inactive";
  if (prevValue === undefined || prevValue === null) return "active";
  const delta = Math.abs(value - prevValue);
  if (delta < 0.01) return "stable";
  if (delta < 0.1) return "changing";
  return "volatile";
}

function ModuleRow({ mod, vals }) {
  const prevRef = useRef();
  const state = getModuleState(vals.dynamic, prevRef.current);
  useEffect(() => { prevRef.current = vals.dynamic; });
  const uiChanged = useHighlightOnChange(vals.ui);
  const dynChanged = useHighlightOnChange(vals.dynamic);
  const totalChanged = useHighlightOnChange(vals.total);
  // Fraîcheur : rendu seul, aucune valeur touchée.
  const fresh = freshness(vals.age_seconds);
  return (
    <tr style={{ borderBottom: "1px solid #044", opacity: fresh.opacity }} title={fresh.title}>
      <td style={{ padding: "6px", display: "flex", alignItems: "center" }}>
        <span className={`state-dot ${state}`} />{mod}
        {fresh.suffix && (
          <span style={{ marginLeft: "6px", fontSize: "0.7em", color: "#888" }}>
            {fresh.suffix}
          </span>
        )}
      </td>
      <td className={uiChanged ? "value-glow" : ""} style={{ textAlign: "right", padding: "6px" }}>
        {(vals.ui || 0) < 0.01 && (vals.ui || 0) > 0 ? (vals.ui).toFixed(6) : (vals.ui || 0).toFixed(2)}
      </td>
      <td className={dynChanged ? "value-glow" : ""} style={{ textAlign: "right", padding: "6px" }}>
        {(vals.dynamic || 0) < 0.01 && (vals.dynamic || 0) > 0 ? (vals.dynamic).toFixed(6) : (vals.dynamic || 0).toFixed(2)}
      </td>
      <td className={totalChanged ? "value-glow" : ""} style={{ textAlign: "right", padding: "6px" }}>
        {(vals.total || 0) < 0.01 && (vals.total || 0) > 0 ? (vals.total).toFixed(6) : (vals.total || 0).toFixed(2)}
      </td>
    </tr>
  );
}

// `entropyTotal` vient du parent, qui le tient de /api/entropy/rich — source de
// vérité unique des totaux d'entropie affichés sur cet écran. /api/oracle_status
// renvoie aussi un `entropy_total`, calculé séparément : il n'est plus lu ici,
// sinon l'écran affiche deux totaux concurrents pour le même système.
function UniversalMonitor({ entropyTotal }) {
  const [data, setData] = React.useState({
    multiversx: { status: "LOADING...", balance: "--", network: "Devnet" },
    ethereum: { status: "LOADING...", balance: "--", network: "Hardhat", price_usd: null },
    polygon: { status: "LOADING...", balance: "--", network: "Amoy", price_usd: null },
    base: { status: "LOADING...", balance: "--", network: "Sepolia", price_usd: null },
    optimism: { status: "LOADING...", balance: "--", network: "Sepolia", price_usd: null },
    entq: { price_eur: 0, price_usd: 0, price_btc: 0, entropy_index: 0, quantum_factor: 0 },
    entropy_total: 0
  });
  const [controls, setControls] = React.useState({ mx: true, eth: true, polygon: true, base: true, optimism: true });
  const [gasStats, setGasStats] = React.useState(null);
  const [masterLoading, setMasterLoading] = React.useState(false);
  const [lastAction, setLastAction] = React.useState(null);

  const toggle = (chain) => {
    const newState = !controls[chain];
    setControls(prev => ({ ...prev, [chain]: newState }));
    secureFetch(`${backendUrl}/api/admin/toggle_network`, {
      method: 'POST', body: JSON.stringify({ network: chain, status: newState })
    }).catch(err => console.error("Erreur communication switch:", err));
  };

  const masterEntropy = (action) => {
    setMasterLoading(true); setLastAction(action);
    secureFetch(`${backendUrl}/api/admin/master-entropy`, {
      method: 'POST', body: JSON.stringify({ action, factor: 1.5 })
    }).then(r => r.json()).then(d => { setTimeout(() => setLastAction(null), 1500); })
      .catch(err => console.error("Master entropy error:", err))
      .finally(() => setMasterLoading(false));
  };

  React.useEffect(() => {
    const tick = setInterval(() => {
      fetch(`${backendUrl}/api/oracle_status`).then(res => { if (!res.ok) throw new Error("Erreur réseau"); return res.json(); })
        .then(async (json) => {
          try {
            // ⚠️ URL RELATIVE, et non plus l'absolue
            //    "https://quantum-oracle-entropie.duckdns.org/rpc".
            //    L'interface est servie depuis DEUX hôtes depuis le 2026-08-05.
            //    Depuis `admin.…`, l'URL absolue partait vers le domaine public :
            //    requête cross-origin, bloquée par CORS (l'hôte admin n'est pas
            //    dans la liste d'origines autorisées) — six erreurs de console
            //    et la chaîne Ethereum affichée hors ligne à tort.
            //    En relatif, l'appel reste sur l'hôte courant, quel qu'il soit,
            //    et le routeur `rpc` de Traefik le sert sur les deux. Aucun
            //    élargissement de la configuration CORS n'est nécessaire.
            const publicRpc = import.meta.env.VITE_RPC_HTTP || "/rpc";
            const check = await fetch(publicRpc, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ jsonrpc: "2.0", method: "net_version", params: [], id: 1 }) });
            if (check.ok) { json.ethereum.status = "ONLINE"; json.ethereum.network = "Hardhat (Secure)"; if (json.ethereum.balance === "--") json.ethereum.balance = "Ready"; }
          } catch (e) { }
          if (!json.base) json.base = { status: "STANDBY", balance: "--", network: "Sepolia", price_usd: null };
          if (!json.optimism) json.optimism = { status: "STANDBY", balance: "--", network: "Sepolia", price_usd: null };
          setData(json);
        }).catch(() => {});

      fetch(`${backendUrl}/api/blockchain/multichain/status`).then(res => res.ok ? res.json() : null)
        .then(mc => {
          if (!mc || !mc.chains) return;
          setData(prev => {
            const updated = { ...prev };
            for (const [key, info] of Object.entries(mc.chains)) {
              const mapped = key.replace('-sepolia', '').replace('-amoy', '').replace('-mainnet', '');
              if (mapped === 'base' || mapped === 'optimism' || mapped === 'polygon') {
                updated[mapped] = { status: info.enabled ? "ONLINE" : "DISABLED", balance: info.balance || "--", network: info.network || key, price_usd: info.price_usd || null };
              }
            }
            return updated;
          });
        }).catch(() => {});

      fetch(`${backendUrl}/api/blockchain/gas-optimizer/config`).then(res => res.ok ? res.json() : null)
        .then(gas => { if (gas && gas.config) setGasStats({ ...gas.config, force_push: gas.config.force_push_enabled ?? false, stats: gas.stats || null }); })
        .catch(() => {});
    }, 3000);
    return () => clearInterval(tick);
  }, []);

  const ControlButton = ({ active, onClick }) => (
    <button onClick={onClick} style={{ background: active ? '#004400' : '#440000', color: active ? '#0f9' : '#f55', border: active ? '1px solid #0f9' : '1px solid #f55', borderRadius: '4px', padding: '2px 8px', fontSize: '0.7rem', cursor: 'pointer', marginLeft: '10px', fontWeight: 'bold', textTransform: 'uppercase', minWidth: '50px' }}>
      {active ? 'ON' : 'OFF'}
    </button>
  );

  const StatusRow = ({ label, info, color, chainKey }) => {
    const isPaused = info.status === 'PAUSED'; const isOnline = info.status === 'ONLINE';
    let statusColor = isPaused ? '#FFA500' : isOnline ? '#0f9' : '#f55';
    return (
      <div style={{ marginBottom: '8px', borderBottom: '1px solid #222', paddingBottom: '4px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem', fontWeight: 'bold' }}>
          <span style={{ color: isPaused ? '#888' : color }}>{label}</span>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {info.price_usd != null && info.price_usd > 0 && <span style={{ color: '#d4af37', fontSize: '0.7rem', marginRight: '5px' }}>${Number(info.price_usd).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>}
            <span style={{ color: statusColor, fontSize: '0.7rem', marginRight: '5px' }}>{info.status}</span>
            <ControlButton active={controls[chainKey]} onClick={() => toggle(chainKey)} />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#aaa', marginTop: '2px' }}>
          <span>{info.network}</span>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            {!isPaused && info.balance && info.balance !== '0' && info.balance !== '--' ? <span>{info.balance}</span> : isPaused ? <span>-</span> : null}
            {!isPaused && info.block_height != null && <span style={{ color: '#666', fontSize: '0.65rem' }}>Block #{Number(info.block_height).toLocaleString()}</span>}
          </div>
        </div>
      </div>
    );
  };

  const entq = data.entq || {};
  return (
    <div>
      <div style={{ marginBottom: '10px', padding: '8px', borderRadius: '6px', background: 'linear-gradient(135deg, #1a0a2e, #0d1117)', border: '1px solid #d4af37', boxShadow: '0 0 12px rgba(212,175,55,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#d4af37', fontWeight: 'bold', fontSize: '0.95rem' }}>🪙 ENTQ</span>
          <span style={{ color: '#d4af37', fontWeight: 'bold', fontSize: '1.1rem' }}>€{Number(entq.price_eur || 0).toFixed(4)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#aaa', marginTop: '3px' }}>
          <span>${Number(entq.price_usd || 0).toFixed(4)}</span>
          <span>₿ {Number(entq.price_btc || 0).toFixed(8)}</span>
          <span style={{ color: '#0ff' }}>Q:{Number(entq.quantum_factor || 0).toFixed(2)}</span>
        </div>
      </div>
      <StatusRow label="⚡ MultiversX" info={data.multiversx} color="#23F7DD" chainKey="mx" />
      <StatusRow label="🔷 Ethereum" info={data.ethereum} color="#627EEA" chainKey="eth" />
      <StatusRow label="🟣 Polygon" info={data.polygon || { status: "N/A", balance: "--", network: "Amoy" }} color="#8247E5" chainKey="polygon" />
      <StatusRow label="🔵 Base" info={data.base || { status: "STANDBY", balance: "--", network: "Sepolia" }} color="#0052FF" chainKey="base" />
      <StatusRow label="🔴 Optimism" info={data.optimism || { status: "STANDBY", balance: "--", network: "Sepolia" }} color="#FF0420" chainKey="optimism" />
      <div style={{ marginTop: '10px', padding: '10px', borderRadius: '6px', background: '#0a0a0a', border: '1px solid #333', textAlign: 'center' }}>
        <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '1px' }}>Entropie Globale (Total)</div>
        <div style={{ fontSize: '1.8rem', fontWeight: 'bold', fontFamily: 'monospace', color: lastAction === 'boost' ? '#0f9' : lastAction === 'reduce' ? '#f55' : '#d4af37', textShadow: lastAction ? '0 0 15px currentColor' : 'none', transition: 'color 0.3s, text-shadow 0.5s' }}>
          {Number(entropyTotal || 0).toFixed(2)}
        </div>
        <div style={{ fontSize: '0.6rem', color: '#555' }}>E.Index: {Number(entq.entropy_index || 0).toFixed(1)}</div>
      </div>
      <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
        <button onClick={() => masterEntropy('boost')} disabled={masterLoading} style={{ flex: 1, padding: '8px', background: '#003300', color: '#0f9', border: '1px solid #0f9', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 'bold', cursor: masterLoading ? 'wait' : 'pointer', opacity: masterLoading ? 0.5 : 1, transition: 'all 0.2s' }}>⊕ BOOST ALL</button>
        <button onClick={() => masterEntropy('reduce')} disabled={masterLoading} style={{ flex: 1, padding: '8px', background: '#330000', color: '#f55', border: '1px solid #f55', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 'bold', cursor: masterLoading ? 'wait' : 'pointer', opacity: masterLoading ? 0.5 : 1, transition: 'all 0.2s' }}>⊖ REDUCE ALL</button>
      </div>
      <button onClick={() => masterEntropy('stabilize')} disabled={masterLoading} style={{ width: '100%', padding: '8px', marginTop: '4px', background: '#001a33', color: '#5bf', border: '1px solid #5bf', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 'bold', cursor: masterLoading ? 'wait' : 'pointer', opacity: masterLoading ? 0.5 : 1, transition: 'all 0.2s' }}>⚖️ STABILISER</button>
      {gasStats && (
        <div style={{ marginTop: '10px', padding: '10px', borderRadius: '6px', background: 'linear-gradient(135deg, #0d1117, #111a2e)', border: '1px solid #f5a623', boxShadow: '0 0 10px rgba(245,166,35,0.15)' }}>
          <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#f5a623', marginBottom: '6px' }}>⛽ Gas & Network Stats</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: '0.7rem' }}>
            <span style={{ color: '#888' }}>Deviation</span><span style={{ color: '#0ff', textAlign: 'right' }}>{gasStats.deviation_threshold_pct != null ? `${gasStats.deviation_threshold_pct}%` : '--'}</span>
            <span style={{ color: '#888' }}>Heartbeat</span><span style={{ color: '#0ff', textAlign: 'right' }}>{gasStats.heartbeat_interval_sec != null ? `${gasStats.heartbeat_interval_sec}s` : '--'}</span>
            <span style={{ color: '#888' }}>Gas Ceiling</span><span style={{ color: (gasStats.max_gas_price_gwei && gasStats.max_gas_price_gwei < 50) ? '#f55' : '#0f9', textAlign: 'right' }}>{gasStats.max_gas_price_gwei != null ? `${gasStats.max_gas_price_gwei} Gwei` : '--'}</span>
            <span style={{ color: '#888' }}>Force Push</span><span style={{ color: gasStats.force_push ? '#f55' : '#0f9', textAlign: 'right', fontWeight: 'bold' }}>{gasStats.force_push ? 'ACTIVE' : 'OFF'}</span>
          </div>
          {gasStats.stats && (
            <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #333' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: '0.65rem' }}>
                <span style={{ color: '#666' }}>Total Checks</span><span style={{ color: '#aaa', textAlign: 'right' }}>{gasStats.stats.total_checks ?? '--'}</span>
                <span style={{ color: '#666' }}>Pushes</span><span style={{ color: '#0f9', textAlign: 'right' }}>{gasStats.stats.total_pushes ?? '--'}</span>
                <span style={{ color: '#666' }}>Skipped</span><span style={{ color: '#f90', textAlign: 'right' }}>{gasStats.stats.skipped_no_deviation ?? '--'}</span>
                <span style={{ color: '#666' }}>Blocked (Gas)</span><span style={{ color: '#f55', textAlign: 'right' }}>{gasStats.stats.blocked_by_ceiling ?? '--'}</span>
                {gasStats.stats.gas_saved_gwei != null && gasStats.stats.gas_saved_gwei > 0 && (<><span style={{ color: '#666' }}>Gas Saved</span><span style={{ color: '#d4af37', textAlign: 'right', fontWeight: 'bold' }}>{Number(gasStats.stats.gas_saved_gwei).toFixed(1)} Gwei</span></>)}
              </div>
            </div>
          )}
        </div>
      )}
      <div style={{ textAlign: 'center', fontSize: '0.6rem', color: '#555', marginTop: '5px' }}>SYNC: {new Date().toLocaleTimeString()}</div>
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [params, setParams] = useState({ rotationSpeed: 1.0, scale: 1.0, color: "#ff0000" });
  const [logs, setLogs] = useState([]);
  const [backendStatus, setBackendStatus] = useState("⏳ Test backend...");
  const [isPlaying, setIsPlaying] = useState(true);
  const [entropyData, setEntropyData] = useState({ entropy_ui: 0, entropy_dyn: 0, entropy_total: 0, modules: {} });
  const [events, setEvents] = useState([]);
  const [newEventName, setNewEventName] = useState("");
  const [newEventTime, setNewEventTime] = useState("");
  const wsStateRef = useRef(null);
  const wsAuditRef = useRef(null);
  const logBoxRef = useRef(null);
  const [benchResult, setBenchResult] = useState(null);
  const [benchLoading, setBenchLoading] = useState(false);
  const [benchError, setBenchError] = useState(null);
  const [modulesError, setModulesError] = useState(null);
  // Second niveau de « Test Puissance » : le banc comparatif, asynchrone
  // (~31 s côté backend). Même bouton, lecture différée.
  const [bancEtat, setBancEtat] = useState(null);
  const bancPollRef = useRef(null);

  // 🛡️ ACCÈS SENTINEL V2 — session admin, plus de mot de passe dédié.
  //
  // Le formulaire précédent postait vers `/api/sentinel/auth`, route retirée en
  // porte 3b : elle validait un mot de passe contre `ORACLE_V2_TOKEN`, jeton
  // statique supprimé en porte 3a. L'URL ne correspondant plus à aucune règle,
  // le POST tombait sur le fallback SPA (`@app.route("/<path:path>")`, GET
  // seul) et recevait un 405 — d'où le « Impossible de joindre le serveur »
  // affiché quel que soit le mot de passe saisi.
  //
  // `isAdmin` vient d'AuthContext, déjà résolu ici : `<AdminGuard>` ne monte
  // cette page qu'après `/api/auth/check`, avec `authenticated && isAdmin`.
  // Aucun appel de vérification supplémentaire n'est donc nécessaire.
  //
  // ⚠️ Ce test est un confort d'affichage, JAMAIS un contrôle d'accès. La garde
  // réelle reste `@login_required` + `@require_admin` côté backend, portée par
  // chaque route sentinel (admin_oracle/sentinel_feed.py, streams/sse_sentinel.py).
  const { isAdmin } = useAuth();

  const handleSentinelClick = () => {
    navigate("/v2/dashboard");
  };

  const fetchEvents = useCallback(async () => {
    try {
      const res = await fetch(`${backendUrl}/api/events`, { credentials: "include" }).catch(() => null);
      if (res && res.ok) { const json = await res.json(); if (Array.isArray(json)) setEvents(json); }
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchEvents(); const interval = setInterval(fetchEvents, 5000); return () => clearInterval(interval); }, [fetchEvents]);

  const addEvent = async () => {
    if (!newEventName || !newEventTime) return;
    try {
      await secureFetch(`${backendUrl}/api/events`, { method: "POST", body: JSON.stringify({ name: newEventName, target_time: newEventTime }), credentials: "include" });
      setNewEventName(""); setNewEventTime(""); fetchEvents();
    } catch (err) { console.error(err); }
  };

  const deleteEvent = async (id) => {
    try { await secureFetch(`${backendUrl}/api/events/${id}`, { method: "DELETE", credentials: "include" }); fetchEvents(); }
    catch (err) { console.error(err); }
  };

  const calculateTimeLeft = (targetStr) => {
    const diff = new Date(targetStr) - new Date();
    if (diff <= 0) return null;
    const m = Math.floor((diff / 1000) / 60); const s = Math.floor((diff / 1000) % 60);
    return `${m}m ${s}s`;
  };

  useEffect(() => {
    fetch(`${backendUrl}/api/health`).then(res => res.json()).then(data => setBackendStatus(data?.status === "ok" ? "✅ Backend OK" : "⚠️ ?!")).catch(() => setBackendStatus("❌ Backend HS"));
    fetch(`${backendUrl}/api/admin-oracle/state`).then(res => res.json()).then(data => data?.rotationSpeed && setParams(data)).catch(console.error);
  }, []);

  useEffect(() => { if (logBoxRef.current) logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight; }, [logs]);

  useEffect(() => {
    const token = getToken(); const qs = token ? `?token=${token}` : "";
    let retryDelay = 1000; const MAX_DELAY = 30000; let isMounted = true;
    function safeClose(ws) {
      if (!ws) return;
      if (ws.readyState === WebSocket.OPEN) ws.close();
      else if (ws.readyState === WebSocket.CONNECTING) ws.addEventListener('open', () => ws.close(), { once: true });
    }
    function connectWs(url, ref, onMessage) {
      if (!isMounted) return;
      const ws = new WebSocket(url); ref.current = ws;
      ws.onopen = () => { if (!isMounted) { ws.close(); return; } retryDelay = 1000; };
      ws.onmessage = (e) => { if (!isMounted) return; onMessage(e); };
      ws.onclose = (e) => { if (!isMounted) return; setTimeout(() => connectWs(url, ref, onMessage), retryDelay); retryDelay = Math.min(retryDelay * 2, MAX_DELAY); };
      ws.onerror = () => { if (!isMounted) return; if (ws.readyState === WebSocket.OPEN) ws.close(); };
    }
    connectWs(`${baseWs}/api/admin-oracle/ws/state${qs}`, wsStateRef, (e) => {
      try { const msg = JSON.parse(e.data); if (msg.type === "state_update") { if (msg.state) setParams(msg.state); if (msg.log) setLogs(prev => [...prev, msg.log]); } } catch (err) { }
    });
    connectWs(`${baseWs}/api/admin-oracle/ws/audit${qs}`, wsAuditRef, (e) => {
      try { const msg = JSON.parse(e.data); if (msg.type === "audit_log") setLogs(prev => [...prev, msg.log]); } catch (err) { }
    });
    return () => {
      isMounted = false;
      safeClose(wsStateRef.current); safeClose(wsAuditRef.current);
      wsStateRef.current = null; wsAuditRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!isPlaying) return;
    const id = setInterval(() => {
      const entropyDyn = Math.abs(params.rotationSpeed) * 120 + Math.random() * 5;
      fetch(`${backendUrl}/api/geometry/cube_dynamics/dyn_entropy`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ entropy_dyn: entropyDyn }) }).catch(() => { });
    }, 5000);
    return () => clearInterval(id);
  }, [isPlaying, params.rotationSpeed]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        // ⚠️ `.catch(() => {})` remplacé (2026-08-07). Il rendait le tableau
        //    Modules muet sur toute panne du flux : la table gardait la
        //    dernière valeur reçue sans jamais dire qu'elle avait cessé d'être
        //    rafraîchie. Un tableau figé ressemble à un tableau à jour.
        fetch(`${backendUrl}/api/entropy/rich`)
          .then(res => lireJson(res, "/api/entropy/rich"))
          .then(data => {
            if (data && data.modules) setEntropyData({ entropy_ui: data.entropy_ui_total || 0, entropy_dyn: data.entropy_dynamic_total || 0, entropy_total: data.entropy_total || 0, modules: data.modules, perf: data.perf });
            setModulesError(null);
          })
          .catch(err => setModulesError(err.message || "Flux des modules interrompu."));
      }
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleChange = (newParams) => {
    setParams(newParams);
    if (wsStateRef.current?.readyState === WebSocket.OPEN) wsStateRef.current.send(JSON.stringify(newParams));
    else secureFetch(`${backendUrl}/api/admin-oracle/state`, { method: "POST", body: JSON.stringify(newParams) });
  };

  // Interroge l'état du banc jusqu'à ce qu'il quitte « en_cours ».
  const suivreBanc = useCallback(() => {
    clearInterval(bancPollRef.current);
    bancPollRef.current = setInterval(async () => {
      try {
        const res = await secureFetch(`${backendUrl}/api/system/banc`);
        const etat = await res.json();
        setBancEtat(etat);
        if (etat.statut !== "en_cours") clearInterval(bancPollRef.current);
      } catch (err) {
        console.error(err);
        clearInterval(bancPollRef.current);
      }
    }, 2000);
  }, []);

  // Un seul déclencheur, deux niveaux de lecture : le stress test s'affiche
  // dès qu'il répond ; le banc comparatif (sous-processus) le rejoint en
  // dessous quand il aboutit.
  //
  // ⚠️ DURÉE — ce commentaire annonçait « ~4 s » pour le stress test. C'est
  //    faux : `trigger_benchmark` (core/entropy_manager.py:233) documente une
  //    trentaine de secondes SYNCHRONES, car il exécute déjà le protocole
  //    complet du banc en plus du débit SHA-256. Le chiffre exact est renvoyé
  //    par la réponse elle-même (`benchmark_duration_s`), c'est la seule
  //    source à croire.
  // ⚠️ ERREURS RENDUES VISIBLES (2026-08-07). Les deux `catch` ne faisaient
  //    qu'un `console.error` : `secureFetch` LÈVE sur toute réponse non-2xx,
  //    donc n'importe quel échec — 401, 403, 405, 500, coupure réseau —
  //    laissait le bouton tourner, s'arrêter, et n'afficher STRICTEMENT RIEN.
  //    Symptôme rapporté en production : « le bouton ne fait rien ». Il fallait
  //    ouvrir la console du navigateur pour découvrir un HTTP 405.
  //
  //    On n'avale plus. Le message vient de `messageErreurHttp`, qui nomme la
  //    cause quand elle est identifiable (mauvais hôte, session expirée, rôle
  //    insuffisant) au lieu de rendre un code nu.
  const runStressTest = async () => {
    setBenchLoading(true); setBenchResult(null); setBancEtat(null); setBenchError(null);
    try {
      const res = await secureFetch(`${backendUrl}/api/system/stress-test`, { method: "POST" });
      setBenchResult(await res.json());
    } catch (err) {
      console.error(err);
      setBenchError(err.message || "Échec du test de puissance.");
      // Le banc ne sert à rien si le stress test n'a pas abouti : il échouerait
      // pour la même raison, et écraserait le message par un doublon.
      setBenchLoading(false);
      return;
    } finally { setBenchLoading(false); }

    // Lancé après le stress test : simultanés, ils fausseraient leurs mesures.
    try {
      const res = await secureFetch(`${backendUrl}/api/system/banc`, { method: "POST" });
      setBancEtat(await res.json());
      suivreBanc();
    } catch (err) {
      console.error(err);
      setBenchError(err.message || "Échec du lancement du banc comparatif.");
    }
  };

  useEffect(() => () => clearInterval(bancPollRef.current), []);

  return (
    <div className="admin-oracle-container" style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>

      {/* HEADER */}
      <div style={{ background: "#111", padding: "10px 20px", borderBottom: "2px solid #333", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: "1.5rem", color: "#fff" }}>🔮 Oracle Admin <span style={{ fontSize: "0.8rem", color: "#888" }}>v2.5</span></h1>
          <div style={{ fontSize: "0.8rem" }}>{backendStatus}</div>
        </div>
        <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "0.8rem", color: "#888" }}>Latency</div>
            <div style={{ fontSize: "1.2rem", color: (entropyData.perf?.latency_ms || 0) < 50 ? "#0f9" : "#f50", fontWeight: "bold" }}>{entropyData.perf?.latency_ms?.toFixed(2) ?? "--"} ms</div>
          </div>
          <div style={{ textAlign: "right", paddingLeft: "15px", borderLeft: "1px solid #444" }}>
            <div style={{ fontSize: "0.8rem", color: "#888" }}>Total Entropy</div>
            <div style={{ fontSize: "1.5rem", color: "#d4af37", fontWeight: "bold" }}>{entropyData.entropy_total?.toFixed(3) ?? "--"}</div>
          </div>
        </div>
      </div>

      {/* CONTENT GRID */}
      <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", flex: 1, overflow: "hidden" }}>
        {/* SIDEBAR */}
        <div className="admin-oracle-sidebar" style={{ width: "100%", overflowY: "auto", borderRight: "1px solid #333", padding: "10px" }}>

          {/* ✅ BOUTON SENTINEL — visible pour la session admin en cours */}
          {isAdmin && (
            <button
              onClick={handleSentinelClick}
              style={{
                display: "block", width: "100%", padding: "14px", marginBottom: "1.5rem",
                background: "linear-gradient(135deg, #003322, #00FF94)",
                color: "#000", border: "1px solid #0f9", borderRadius: "8px",
                fontWeight: "bold", fontSize: "0.95rem",
                boxShadow: "0 0 20px rgba(0,255,148,0.4)",
                textTransform: "uppercase", cursor: "pointer", transition: "all 0.2s ease-in-out",
                letterSpacing: "1px"
              }}
              onMouseOver={e => { e.currentTarget.style.transform = "scale(1.03)"; e.currentTarget.style.boxShadow = "0 0 30px rgba(0,255,148,0.7)"; }}
              onMouseOut={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 0 20px rgba(0,255,148,0.4)"; }}
            >
              🛡️ ACCÉDER AU SENTINEL V2
            </button>
          )}

          {/* ADMIN API QUOTA */}
          <Link to="/hub/api-quota" style={{ textDecoration: 'none' }}>
            <button
              style={{
                display: "block", width: "100%", padding: "12px", marginBottom: "1.5rem",
                background: "#1a1a2e", color: "#00ff94", border: "1px solid #00ff94", borderRadius: "8px",
                fontWeight: "bold", fontSize: "0.95rem", cursor: "pointer", transition: "all 0.2s"
              }}
              onMouseOver={e => { e.currentTarget.style.background = "#003322"; }}
              onMouseOut={e => { e.currentTarget.style.background = "#1a1a2e"; }}
            >
              📊 SUPERVISION QUOTA API
            </button>
          </Link>

          {/* STRESS TEST */}
          <div className="admin-oracle-panel" style={{ marginBottom: "1rem", border: "1px solid #444", padding: "10px", background: "#1a1a1a" }}>
            <h3 style={{ marginTop: 0, color: "#ff0055" }}>🔥 Test Puissance</h3>
            <button onClick={runStressTest} disabled={benchLoading} style={{ width: "100%", padding: "8px", background: benchLoading ? "#333" : "#ff0055", color: "#fff", border: "none", fontWeight: "bold", cursor: "pointer" }}>
              {benchLoading ? "En cours..." : "LANCER TEST"}
            </button>
            {benchError && (
              <div style={{ marginTop: "8px", padding: "8px", background: "#2a0d13", border: "1px solid #ff4d4d", borderRadius: "4px", color: "#ff9d9d", fontSize: "0.78rem", lineHeight: 1.4 }}>
                ⚠️ {benchError}
              </div>
            )}
            {benchResult && <div style={{ marginTop: "5px", color: "#0f9", fontSize: "0.8rem" }}>{benchResult.details}</div>}
            {benchLoading && <div style={{ marginTop: "5px", color: "#888", fontSize: "0.75rem" }}>Débit + protocole du banc (14 modules) — une trentaine de secondes.</div>}
            <BancPanel result={benchResult} />
            <BancComparatif etat={bancEtat} />
          </div>

          {/* PARAMS */}
          <div className="admin-oracle-panel" style={{ marginBottom: "1rem", padding: "10px", background: "#1a1a1a" }}>
            <h3>⚙️ Cube Params</h3>
            <div style={{ marginBottom: "5px" }}><label>Speed</label><input type="range" min="0.1" max="5" step="0.1" value={params.rotationSpeed} onChange={(e) => handleChange({ ...params, rotationSpeed: parseFloat(e.target.value) })} style={{ width: "100%" }} /></div>
            <div style={{ marginBottom: "5px" }}><label>Scale</label><input type="range" min="0.1" max="5" step="0.1" value={params.scale} onChange={(e) => handleChange({ ...params, scale: parseFloat(e.target.value) })} style={{ width: "100%" }} /></div>
            <div><label>Color</label><input type="color" value={params.color} onChange={(e) => handleChange({ ...params, color: e.target.value })} /></div>
            <button onClick={() => setIsPlaying(!isPlaying)} style={{ marginTop: "10px", width: "100%" }}>{isPlaying ? "PAUSE ANIMATION" : "PLAY ANIMATION"}</button>
          </div>

          <div className="admin-oracle-panel" style={{ marginTop: "1rem", borderTop: "2px solid #23F7DD", padding: "10px", background: "#112" }}>
            <h3 style={{ margin: "0 0 10px 0" }}>🧭 ORACLE UNIVERSEL</h3>
            <UniversalMonitor entropyTotal={entropyData.entropy_total} />
          </div>

          <div className="admin-oracle-panel" style={{ marginTop: "1rem", padding: "10px", background: "#1a1a1a" }}>
            <h3>🔮 Events</h3>
            <div style={{ display: "flex", gap: "5px" }}>
              <input type="text" placeholder="Nom" value={newEventName} onChange={e => setNewEventName(e.target.value)} style={{ width: "80px" }} />
              <input type="datetime-local" value={newEventTime} onChange={e => setNewEventTime(e.target.value)} style={{ flex: 1 }} />
            </div>
            <button onClick={addEvent} style={{ width: "100%", marginTop: "5px" }}>Ajouter</button>
            <ul style={{ padding: 0, listStyle: "none", fontSize: "0.8rem", maxHeight: "180px", overflowY: "auto" }}>
              {events.map(ev => (
                <li key={ev.id} style={{ display: "flex", justifyContent: "space-between", marginTop: "5px" }}>
                  <span>{ev.name}</span>
                  <span style={{ color: "#0ff" }}>{calculateTimeLeft(ev.target_time) || "Fini"} <span onClick={() => deleteEvent(ev.id)} style={{ cursor: "pointer" }}>❌</span></span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div style={{ padding: "20px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "20px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", height: "300px" }}>
            <div className="admin-oracle-panel" style={{ background: "#000", border: "1px solid #333" }}>
              <AdminOracleState3D state={{ ...params, rotationSpeed: isPlaying ? params.rotationSpeed : 0 }} />
            </div>
            <div className="admin-oracle-panel" style={{ display: "flex", flexDirection: "column", background: "#111", padding: "10px" }}>
              <h3>📜 Audit Log</h3>
              <div ref={logBoxRef} style={{ flex: 1, overflowY: "auto", fontSize: "0.8rem", fontFamily: "monospace" }}>
                {logs.slice(-50).map((l, i) => <div key={i} style={{ marginBottom: "2px" }}><span style={{ color: "#888" }}>{l.timestamp}</span> {l.message}</div>)}
              </div>
            </div>
          </div>
          <div className="admin-oracle-panel" style={{ padding: "10px", background: "#1a1a1a" }}>
            <h3>📊 Modules</h3>
            {modulesError && (
              <div style={{ marginBottom: "8px", padding: "8px", background: "#2a0d13", border: "1px solid #ff4d4d", borderRadius: "4px", color: "#ff9d9d", fontSize: "0.78rem", lineHeight: 1.4 }}>
                ⚠️ {modulesError} — les valeurs ci-dessous sont figées.
              </div>
            )}
            <table style={{ width: "100%", color: "#ccc", fontSize: "0.9rem" }}>
              <thead><tr style={{ textAlign: "right" }}><th style={{ textAlign: "left" }}>Name</th><th>UI</th><th>Dyn</th><th>Total</th></tr></thead>
              <tbody>{Object.entries(entropyData.modules || {}).map(([k, v]) => <ModuleRow key={k} mod={k} vals={v} />)}</tbody>
            </table>
          </div>
        </div>
      </div>
      <style>{`@keyframes pulseWidth { 0% { opacity:0.5; } 50% { opacity:1; } 100% { opacity:0.5; } }`}</style>
    </div>
  );
}
